import React from "react";
import type { Intent, PublishStatus, LeadStatus, ComplaintStatus, Severity } from "@/lib/types";

type ChipVariant = "neutral" | "blue" | "violet" | "green" | "amber" | "red" | "slate";

const styles: Record<ChipVariant, string> = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-800 text-white border-slate-800",
};

export const Chip: React.FC<{
  variant?: ChipVariant;
  children: React.ReactNode;
  testId?: string;
}> = ({ variant = "neutral", children, testId }) => (
  <span data-testid={testId} className={`sg-chip ${styles[variant]}`}>
    {children}
  </span>
);

const INTENT_MAP: Record<Intent, { variant: ChipVariant; label: string }> = {
  buying_intent: { variant: "violet", label: "Buying intent" },
  product_question: { variant: "blue", label: "Question" },
  complaint: { variant: "red", label: "Complaint" },
  neutral: { variant: "neutral", label: "Neutral" },
  spam: { variant: "slate", label: "Spam" },
};

export const IntentChip: React.FC<{ intent?: Intent | null; testId?: string }> = ({
  intent,
  testId,
}) => {
  if (!intent) return <Chip testId={testId}>Unclassified</Chip>;
  const cfg = INTENT_MAP[intent];
  return (
    <Chip variant={cfg.variant} testId={testId}>
      {cfg.label}
    </Chip>
  );
};

const PUBLISH_MAP: Record<PublishStatus, { variant: ChipVariant; label: string }> = {
  pending: { variant: "amber", label: "Pending" },
  publishing: { variant: "blue", label: "Publishing" },
  published: { variant: "green", label: "Published" },
  failed: { variant: "red", label: "Failed" },
  retrying: { variant: "amber", label: "Retrying" },
};

export const PublishChip: React.FC<{ status: PublishStatus; testId?: string }> = ({
  status,
  testId,
}) => {
  const cfg = PUBLISH_MAP[status];
  return (
    <Chip variant={cfg.variant} testId={testId}>
      {cfg.label}
    </Chip>
  );
};

const LEAD_MAP: Record<LeadStatus, { variant: ChipVariant; label: string }> = {
  new: { variant: "violet", label: "New" },
  contacted: { variant: "blue", label: "Contacted" },
  qualified: { variant: "amber", label: "Qualified" },
  converted: { variant: "green", label: "Converted" },
  closed: { variant: "neutral", label: "Closed" },
};

export const LeadChip: React.FC<{ status: LeadStatus; testId?: string }> = ({
  status,
  testId,
}) => {
  const cfg = LEAD_MAP[status];
  return (
    <Chip variant={cfg.variant} testId={testId}>
      {cfg.label}
    </Chip>
  );
};

const COMPLAINT_MAP: Record<ComplaintStatus, { variant: ChipVariant; label: string }> = {
  open: { variant: "red", label: "Open" },
  in_review: { variant: "amber", label: "In review" },
  resolved: { variant: "green", label: "Resolved" },
  ignored: { variant: "neutral", label: "Ignored" },
};

export const ComplaintChip: React.FC<{ status: ComplaintStatus; testId?: string }> = ({
  status,
  testId,
}) => {
  const cfg = COMPLAINT_MAP[status];
  return (
    <Chip variant={cfg.variant} testId={testId}>
      {cfg.label}
    </Chip>
  );
};

const SEVERITY_MAP: Record<Severity, { variant: ChipVariant; label: string }> = {
  low: { variant: "neutral", label: "Low" },
  medium: { variant: "amber", label: "Medium" },
  high: { variant: "red", label: "High" },
};

export const SeverityChip: React.FC<{ severity?: Severity | null; testId?: string }> = ({
  severity,
  testId,
}) => {
  if (!severity) return null;
  const cfg = SEVERITY_MAP[severity];
  return (
    <Chip variant={cfg.variant} testId={testId}>
      {cfg.label}
    </Chip>
  );
};

const PLATFORM_MAP: Record<string, ChipVariant> = {
  youtube: "red",
  instagram: "violet",
  facebook: "blue",
};

export const PlatformChip: React.FC<{ platform: string; testId?: string }> = ({
  platform,
  testId,
}) => (
  <Chip variant={PLATFORM_MAP[platform] || "neutral"} testId={testId}>
    {platform.charAt(0).toUpperCase() + platform.slice(1)}
  </Chip>
);
